/**
 * src/RegistryComponents.js
 *
 * VISINVOKE Windows Knowledge Centre - reusable placeholder components.
 *
 * Structure only. No registry functionality is implemented in this stage.
 * Every component below uses the existing VISINVOKE Utilities design language
 * (card gradient, white launch button) refined for readability and per-category
 * visual identity. Each category carries an accent colour from registryConfig
 * which drives a thin neon border, a soft outer glow, the icon colour, and the
 * hover brightening. Layout, spacing structure, and component placement are
 * unchanged from the previous stage.
 */
import React from 'react';
import { glassSurface, viGlassFill, viGlassBorder, viGlassShadow } from './config/glassTheme';
import { REGISTRY_DEFAULTS, getStatusColor } from './registryConfig';

// Convert a hex colour + alpha (0..1) to an rgba() string for glows/tints.
export function hexA(hex, alpha) {
  let h = (hex || '#ffffff').replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Shared style object. Values refined for contrast and hierarchy.
export const rg = {
  // Hero
  heroWrap: {
    background: 'linear-gradient(160deg, #0d1117 0%, #131c2e 50%, #0d1117 100%)',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    padding: '48px 0 38px',
  },
  heroInner: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 40px',
    textAlign: 'center',
  },
  heroEyebrow: {
    fontSize: 13,
    fontWeight: 800,
    color: '#7dd3fc',
    textTransform: 'uppercase',
    letterSpacing: '0.18em',
    margin: '0 0 14px',
  },
  heroTitle: {
    fontSize: 46,
    fontWeight: 900,
    color: '#ffffff',
    lineHeight: 1.12,
    margin: 0,
    marginBottom: 12,
    letterSpacing: '-0.6px',
  },
  heroSubtitle: {
    fontSize: 18,
    fontWeight: 500,
    color: '#cbd5e1',
    lineHeight: 1.55,
    margin: '0 auto',
    maxWidth: 720,
  },

  // Generic section
  section: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 40px',
  },
  sectionTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: '#ffffff',
    letterSpacing: '-0.3px',
    margin: '0 0 8px',
  },
  sectionDesc: {
    fontSize: 15,
    color: '#cbd5e1',
    lineHeight: 1.7,
    margin: '0 0 4px',
    maxWidth: 820,
  },

  // Information panel
  infoPanel: {
    background: 'linear-gradient(160deg, #101a2e 0%, #1e3a8a 45%, #1d4ed8 80%, #2563eb 100%)',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 12,
    padding: '24px 22px',
    boxShadow: '0 6px 24px rgba(0,0,0,0.42)',
    textAlign: 'left',
  },
  infoPanelTitle: {
    fontWeight: 800,
    fontSize: 17,
    color: '#ffffff',
    lineHeight: 1.3,
    margin: '0 0 8px',
    letterSpacing: '-0.2px',
  },
  infoPanelLead: {
    fontSize: 14,
    color: '#e2e8f0',
    lineHeight: 1.65,
    margin: '0 0 18px',
  },
  infoList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 14,
    color: '#f1f5f9',
    lineHeight: 1.5,
    fontWeight: 500,
  },
  infoDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    flexShrink: 0,
  },

  // Category grid + card
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 18,
  },
  card: {
    background: 'linear-gradient(160deg, #101a2e 0%, #1e3a8a 45%, #1d4ed8 80%, #2563eb 100%)',
    borderRadius: 12,
    padding: '22px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    textAlign: 'left',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
  },
  cardIcon: {
    fontSize: 24,
    lineHeight: 1,
    flexShrink: 0,
    marginTop: 2,
    transition: 'filter 200ms ease',
  },
  cardTitle: {
    fontWeight: 800,
    fontSize: 16,
    color: '#ffffff',
    lineHeight: 1.3,
    marginBottom: 4,
    letterSpacing: '-0.2px',
  },
  cardDesc: {
    fontSize: 13,
    color: '#dbe4f0',
    lineHeight: 1.6,
  },
  cardMeta: {
    marginTop: 'auto',
    paddingTop: 14,
    borderTop: '1px solid rgba(255,255,255,0.10)',
    display: 'flex',
    flexDirection: 'column',
    gap: 9,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  metaLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#aab8c9',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
  },
  metaValue: {
    fontSize: 13,
    fontWeight: 700,
    color: '#ffffff',
  },
  cardActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    paddingTop: 2,
  },
  statusBadge: {
    fontSize: 10,
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    border: '1px solid',
    borderRadius: 6,
    padding: '4px 9px',
    flexShrink: 0,
  },
  openBtn: {
    background: '#ffffff',
    color: '#0f172a',
    border: 'none',
    borderRadius: 8,
    padding: '9px 16px',
    fontSize: 12,
    fontWeight: 800,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
    whiteSpace: 'nowrap',
    transition: 'box-shadow 200ms ease, transform 160ms ease',
  },

  // Search / filter / sort placeholders
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  searchInput: {
    flex: 1,
    minWidth: 220,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 8,
    padding: '11px 15px',
    fontSize: 14,
    color: '#e2e8f0',
    outline: 'none',
  },
  select: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 8,
    padding: '11px 15px',
    fontSize: 14,
    color: '#cbd5e1',
    outline: 'none',
    cursor: 'not-allowed',
  },

  // Empty state
  empty: {
    background: 'rgba(255,255,255,0.04)',
    border: '1px dashed rgba(255,255,255,0.18)',
    borderRadius: 12,
    padding: '52px 24px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: 34,
    lineHeight: 1,
    marginBottom: 14,
    opacity: 0.85,
  },
  emptyText: {
    fontSize: 15,
    color: '#cbd5e1',
    lineHeight: 1.6,
    margin: 0,
    maxWidth: 460,
    marginLeft: 'auto',
    marginRight: 'auto',
    fontWeight: 500,
  },

  // Tweak card placeholder (structure only, not rendered this stage)
  tweakCard: {
    background: 'linear-gradient(160deg, #101a2e 0%, #1e3a8a 45%, #1d4ed8 80%, #2563eb 100%)',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 12,
    padding: '20px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    boxShadow: '0 6px 24px rgba(0,0,0,0.42)',
    textAlign: 'left',
  },
  tweakTitle: {
    fontWeight: 800,
    fontSize: 15,
    color: '#ffffff',
    lineHeight: 1.3,
  },
  tweakDesc: {
    fontSize: 13,
    color: '#dbe4f0',
    lineHeight: 1.6,
  },
};

// Hero: eyebrow + large title + subtitle, optional intro panel as children.
export function RegistryHero({ eyebrow, title, subtitle, children }) {
  return (
    <div style={rg.heroWrap}>
      <div style={rg.heroInner}>
        {eyebrow ? <div style={rg.heroEyebrow}>{eyebrow}</div> : null}
        <h1 style={rg.heroTitle}>{title}</h1>
        {subtitle ? <p style={rg.heroSubtitle}>{subtitle}</p> : null}
      </div>
      {children}
    </div>
  );
}

// Category tile. Accent drives the neon border, glow, icon colour, and hover.
export function RegistryCategoryCard({ category, onOpen }) {
  const accent = category.accent || '#f8fafc';
  const status = category.status || REGISTRY_DEFAULTS.status;
  const statusColor = getStatusColor(status);
  const tweaksAvailable =
    category.tweaksAvailable !== undefined
      ? category.tweaksAvailable
      : REGISTRY_DEFAULTS.tweaksAvailable;
  const lastUpdated = category.lastUpdated || REGISTRY_DEFAULTS.lastUpdated;

  const restBorder = hexA(accent, 0.45);
  const restGlow = `0 4px 20px rgba(0,0,0,0.40), 0 0 0 1px ${hexA(accent, 0.18)}, 0 0 14px ${hexA(accent, 0.14)}`;
  const hoverBorder = accent;
  const hoverGlow = `0 12px 34px rgba(0,0,0,0.46), 0 0 0 1px ${hexA(accent, 0.55)}, 0 0 22px ${hexA(accent, 0.34)}`;

  const onEnter = e => {
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.borderColor = hoverBorder;
    e.currentTarget.style.boxShadow = hoverGlow;
    const ic = e.currentTarget.querySelector('[data-cat-icon]');
    if (ic) ic.style.filter = 'brightness(1.35) saturate(1.2)';
  };
  const onLeave = e => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.borderColor = restBorder;
    e.currentTarget.style.boxShadow = restGlow;
    const ic = e.currentTarget.querySelector('[data-cat-icon]');
    if (ic) ic.style.filter = 'none';
  };

  return (
    <div
      style={{
        ...rg.card,
        background: viGlassFill(accent, accent, accent),
        border: viGlassBorder(accent, 1.5),
        boxShadow: restGlow,
        transition: 'transform 220ms cubic-bezier(0.22,1,0.36,1), box-shadow 220ms ease, border-color 220ms ease',
      }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div style={rg.cardHeader}>
        <div data-cat-icon style={{ ...rg.cardIcon, color: accent, textShadow: `0 0 12px ${hexA(accent, 0.55)}` }}>
          {category.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={rg.cardTitle}>{category.title}</div>
          <div style={rg.cardDesc}>{category.description}</div>
        </div>
      </div>

      <div style={rg.cardMeta}>
        <div style={rg.metaRow}>
          <span style={rg.metaLabel}>Status</span>
          <span
            style={{
              ...rg.statusBadge,
              ...glassSurface(statusColor, statusColor, statusColor),
            }}
          >
            {status}
          </span>
        </div>
        <div style={rg.metaRow}>
          <span style={rg.metaLabel}>Tweaks Available</span>
          <span style={rg.metaValue}>{tweaksAvailable}</span>
        </div>
        <div style={rg.metaRow}>
          <span style={rg.metaLabel}>Last Updated</span>
          <span style={rg.metaValue}>{lastUpdated}</span>
        </div>
      </div>

      <div style={rg.cardActions}>
        <button
          style={{ ...rg.openBtn, ...glassSurface(accent, accent, accent) }}
          onClick={() => onOpen && onOpen(category)}
          onMouseEnter={e => { e.currentTarget.style.boxShadow = viGlassShadow(accent, accent, accent, true); e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.boxShadow = viGlassShadow(accent, accent, accent, false); e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          Open Category
        </button>
      </div>
    </div>
  );
}

// Information panel: lists what every entry will provide. Coloured bullets.
export function RegistryInformationPanel({ title, lead, items, accent }) {
  const bullet = accent || '#7dd3fc';
  return (
    <div style={rg.infoPanel}>
      {title ? <div style={rg.infoPanelTitle}>{title}</div> : null}
      {lead ? <p style={rg.infoPanelLead}>{lead}</p> : null}
      <ul style={rg.infoList}>
        {(items || []).map((item, i) => (
          <li key={i} style={rg.infoItem}>
            <span style={{ ...rg.infoDot, background: bullet, boxShadow: `0 0 8px ${hexA(bullet, 0.7)}` }} />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Search placeholder. Non-functional this stage.
export function RegistrySearchBar({ placeholder }) {
  return (
    <input
      type="text"
      disabled
      style={rg.searchInput}
      placeholder={placeholder || 'Search the Windows Knowledge Centre...'}
    />
  );
}

// Filter + sort placeholders. Non-functional this stage.
export function RegistryFilterBar({ filterLabel, sortLabel }) {
  return (
    <React.Fragment>
      <select disabled style={rg.select}>
        <option>{filterLabel || 'Filter'}</option>
      </select>
      <select disabled style={rg.select}>
        <option>{sortLabel || 'Sort'}</option>
      </select>
    </React.Fragment>
  );
}

// Empty state shown when a category has no entries yet.
export function RegistryEmptyState({ message }) {
  return (
    <div style={rg.empty}>
      <div style={rg.emptyIcon}>{'\u{1F9F0}'}</div>
      <p style={rg.emptyText}>
        {message || 'Entries for this category will appear here as they are added.'}
      </p>
    </div>
  );
}

// Placeholder tweak card. Structure only. Not rendered in this stage.
export function RegistryTweakCard({ tweak }) {
  const t = tweak || {};
  return (
    <div style={rg.tweakCard}>
      <div style={rg.tweakTitle}>{t.title || 'Knowledge Centre Entry'}</div>
      <div style={rg.tweakDesc}>{t.description || ''}</div>
    </div>
  );
}

// Generic titled section wrapper.
export function RegistrySection({ title, description, children }) {
  return (
    <div style={rg.section}>
      {title ? <h2 style={rg.sectionTitle}>{title}</h2> : null}
      {description ? <p style={rg.sectionDesc}>{description}</p> : null}
      {children}
    </div>
  );
}

// ============================================================================
// WINDOWS KNOWLEDGE CENTRE - category experience components (Stage XX-B)
//
// Reusable across every category. Structure only - no content, no search,
// no filtering, no backend. Knowledge-base ready: components speak in terms
// of articles / tweaks / tools, not registry tweaks alone. Accent colour is
// passed in so every section carries the category's visual identity.
// ============================================================================

// Knowledge Centre style namespace. Kept separate from rg to avoid touching
// existing styles. Mirrors the same design language (card gradient, contrast).
export const kc = {
  // Section wrapper
  block: {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 40px',
    width: '100%',
    boxSizing: 'border-box',
  },
  blockHead: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  h2: {
    fontSize: 24,
    fontWeight: 800,
    color: '#ffffff',
    letterSpacing: '-0.3px',
    margin: 0,
  },
  h2Sub: {
    fontSize: 14,
    color: '#cbd5e1',
    fontWeight: 500,
    margin: '6px 0 0',
    lineHeight: 1.6,
  },
  counter: {
    fontSize: 13,
    fontWeight: 700,
    color: '#aab8c9',
  },

  // Stat cards
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
  },
  statCard: {
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    borderRadius: 12,
    padding: '20px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  statValue: {
    fontSize: 30,
    fontWeight: 900,
    color: '#ffffff',
    lineHeight: 1.1,
    letterSpacing: '-0.5px',
  },
  statLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: '#cbd5e1',
  },

  // Generic card surface
  panel: {
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    borderRadius: 12,
    padding: '24px 24px',
  },
  panelTitle: {
    fontSize: 17,
    fontWeight: 800,
    color: '#ffffff',
    margin: '0 0 14px',
    letterSpacing: '-0.2px',
  },

  // Quick start list
  qsList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  qsItem: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
  },
  qsIcon: {
    fontSize: 18,
    lineHeight: 1.2,
    flexShrink: 0,
  },
  qsLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: 2,
  },
  qsText: {
    fontSize: 14,
    color: '#dbe4f0',
    lineHeight: 1.6,
  },

  // Inline empty content panel
  emptyPanel: {
    borderRadius: 12,
    padding: '40px 24px',
    textAlign: 'center',
    background: 'rgba(255,255,255,0.04)',
    border: '1px dashed rgba(255,255,255,0.18)',
  },
  emptyPanelIcon: {
    fontSize: 30,
    lineHeight: 1,
    marginBottom: 12,
    opacity: 0.9,
  },
  emptyPanelTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: '#ffffff',
    margin: '0 0 6px',
  },
  emptyPanelText: {
    fontSize: 14,
    color: '#cbd5e1',
    lineHeight: 1.6,
    margin: 0,
    maxWidth: 520,
    marginLeft: 'auto',
    marginRight: 'auto',
    fontWeight: 500,
  },

  // Library controls
  controls: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 18,
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  search: {
    flex: 1,
    minWidth: 240,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 8,
    padding: '11px 15px',
    fontSize: 14,
    color: '#e2e8f0',
    outline: 'none',
  },
  select: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 8,
    padding: '11px 15px',
    fontSize: 13,
    color: '#cbd5e1',
    outline: 'none',
    cursor: 'not-allowed',
  },
  viewToggle: {
    display: 'flex',
    gap: 4,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.16)',
    borderRadius: 8,
    padding: 3,
  },
  viewBtn: {
    border: 'none',
    background: 'transparent',
    color: '#cbd5e1',
    fontSize: 12,
    fontWeight: 700,
    padding: '7px 12px',
    borderRadius: 6,
    cursor: 'not-allowed',
  },
  viewBtnActive: {
    border: 'none',
    color: '#0f172a',
    fontSize: 12,
    fontWeight: 700,
    padding: '7px 12px',
    borderRadius: 6,
    cursor: 'not-allowed',
  },

  // Related categories
  relGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 14,
  },
  relCard: {
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    borderRadius: 11,
    padding: '16px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    cursor: 'pointer',
    transition: 'transform 200ms cubic-bezier(0.22,1,0.36,1), box-shadow 200ms ease, border-color 200ms ease',
  },
  relIcon: {
    fontSize: 22,
    lineHeight: 1,
    flexShrink: 0,
    transition: 'filter 200ms ease',
  },
  relTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#ffffff',
    lineHeight: 1.3,
  },

  // FAQ
  faqList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  faqItem: {
    background: 'linear-gradient(160deg, #101a2e 0%, #16243d 100%)',
    borderRadius: 11,
    padding: '18px 20px',
  },
  faqQ: {
    fontSize: 15,
    fontWeight: 800,
    color: '#ffffff',
    margin: '0 0 8px',
    letterSpacing: '-0.1px',
  },
  faqA: {
    fontSize: 14,
    color: '#dbe4f0',
    lineHeight: 1.65,
    margin: 0,
  },

  // Safety centre
  safetyList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 11,
  },
  safetyItem: {
    display: 'flex',
    gap: 11,
    alignItems: 'flex-start',
    fontSize: 14,
    color: '#f1f5f9',
    lineHeight: 1.55,
    fontWeight: 500,
  },
  safetyMark: {
    fontSize: 15,
    lineHeight: 1.4,
    flexShrink: 0,
  },
};

// Generic inline empty-state panel used by content sections.
export function EmptyContentPanel({ icon, title, message, accent }) {
  const a = accent || '#7dd3fc';
  return (
    <div style={{ ...kc.emptyPanel, borderColor: hexA(a, 0.28) }}>
      <div style={{ ...kc.emptyPanelIcon, color: a, textShadow: `0 0 12px ${hexA(a, 0.5)}` }}>
        {icon || '\u{1F4DA}'}
      </div>
      <div style={kc.emptyPanelTitle}>{title || 'Nothing here yet'}</div>
      <p style={kc.emptyPanelText}>{message || 'Content will appear here as the library grows.'}</p>
    </div>
  );
}

// Four dynamic summary stat cards. Each value is a placeholder.
export function CategoryStats({ stats, accent }) {
  const a = accent || '#7dd3fc';
  return (
    <div style={kc.statGrid}>
      {(stats || []).map((s, i) => (
        <div
          key={s.id || i}
          style={{ ...kc.statCard, border: `1px solid ${hexA(a, 0.30)}`, boxShadow: `0 0 0 1px ${hexA(a, 0.12)}, 0 0 14px ${hexA(a, 0.10)}` }}
        >
          <div style={{ ...kc.statValue, color: '#ffffff' }}>
            {s.value}{s.suffix || ''}
          </div>
          <div style={kc.statLabel}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

// Featured content section. Empty state until the library grows.
export function FeaturedContentSection({ accent }) {
  return (
    <div style={kc.block}>
      <div style={kc.blockHead}>
        <div>
          <h2 style={kc.h2}>Featured Content</h2>
          <p style={kc.h2Sub}>Hand-picked articles and tweaks worth starting with.</p>
        </div>
      </div>
      <EmptyContentPanel
        icon={'\u{2B50}'}
        title="Featured content is on the way"
        message="Featured articles and tweaks will appear here as the library grows. Check back soon."
        accent={accent}
      />
    </div>
  );
}

// Recently added section. Placeholder only.
export function RecentContentSection({ accent }) {
  return (
    <div style={kc.block}>
      <div style={kc.blockHead}>
        <div>
          <h2 style={kc.h2}>Recently Added</h2>
          <p style={kc.h2Sub}>The newest additions to this category.</p>
        </div>
      </div>
      <EmptyContentPanel
        icon={'\u{1F195}'}
        title="No recent entries yet"
        message="Newly added articles, guides and tweaks will appear here as content is published."
        accent={accent}
      />
    </div>
  );
}

// Most popular section. Placeholder only.
export function PopularContentSection({ accent }) {
  return (
    <div style={kc.block}>
      <div style={kc.blockHead}>
        <div>
          <h2 style={kc.h2}>Most Popular</h2>
          <p style={kc.h2Sub}>The most viewed and most used entries in this category.</p>
        </div>
      </div>
      <EmptyContentPanel
        icon={'\u{1F525}'}
        title="Popularity data is building"
        message="The most popular articles and tweaks will appear here once entries are added and used."
        accent={accent}
      />
    </div>
  );
}

// Quick start / getting started panel. Content passed in as items.
export function QuickStartSection({ items, accent }) {
  return (
    <div style={kc.block}>
      <div style={kc.blockHead}>
        <div>
          <h2 style={kc.h2}>Getting Started</h2>
          <p style={kc.h2Sub}>What this category covers and how to use it safely.</p>
        </div>
      </div>
      <div style={{ ...kc.panel, border: `1px solid ${hexA(accent || '#7dd3fc', 0.30)}` }}>
        <ul style={kc.qsList}>
          {(items || []).map((it, i) => (
            <li key={i} style={kc.qsItem}>
              <span style={{ ...kc.qsIcon, color: accent || '#7dd3fc' }}>{it.icon}</span>
              <div>
                <div style={kc.qsLabel}>{it.label}</div>
                <div style={kc.qsText}>{it.text}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Complete library. Permanent interface only - no functionality this stage.
export function KnowledgeLibrary({ categoryFilters, difficultyLevels, windowsVersions, sortOptions, accent }) {
  const a = accent || '#7dd3fc';
  return (
    <div style={kc.block}>
      <div style={kc.blockHead}>
        <div>
          <h2 style={kc.h2}>Complete Library</h2>
          <p style={kc.h2Sub}>Every article, guide and tweak in this category, in one place.</p>
        </div>
        <span style={kc.counter}>0 results</span>
      </div>

      <div style={{ ...kc.panel, border: `1px solid ${hexA(a, 0.24)}` }}>
        {/* Controls (placeholders only) */}
        <div style={kc.controls}>
          <div style={kc.controlsRow}>
            <input type="text" disabled style={kc.search} placeholder="Search this category..." />
            <div style={kc.viewToggle}>
              <button disabled style={{ ...kc.viewBtnActive, background: a }}>Cards</button>
              <button disabled style={kc.viewBtn}>Compact</button>
            </div>
          </div>
          <div style={kc.controlsRow}>
            <select disabled style={kc.select}>
              <option>{(categoryFilters && categoryFilters[0]) || 'All Content'}</option>
            </select>
            <select disabled style={kc.select}>
              <option>{(difficultyLevels && difficultyLevels[0]) || 'All Levels'}</option>
            </select>
            <select disabled style={kc.select}>
              <option>{(windowsVersions && `All Versions`) || 'All Versions'}</option>
            </select>
            <select disabled style={kc.select}>
              <option>{(sortOptions && sortOptions[0]) || 'Newest'}</option>
            </select>
          </div>
        </div>

        {/* Empty state */}
        <EmptyContentPanel
          icon={'\u{1F4D6}'}
          title="The library is empty for now"
          message="Articles, guides and tweaks for this category will appear here as they are added. Search, filters and views will activate once content is available."
          accent={a}
        />
      </div>
    </div>
  );
}

// Safety centre information card.
export function SafetyCentre({ items, accent }) {
  const a = accent || '#34d399';
  return (
    <div style={kc.block}>
      <div style={kc.blockHead}>
        <div>
          <h2 style={kc.h2}>Safety Centre</h2>
          <p style={kc.h2Sub}>Read this before applying any change.</p>
        </div>
      </div>
      <div style={{ ...kc.panel, border: `1px solid ${hexA(a, 0.34)}`, boxShadow: `0 0 0 1px ${hexA(a, 0.14)}, 0 0 16px ${hexA(a, 0.12)}` }}>
        <div style={kc.panelTitle}>{'\u{1F6E1}'} Safety First</div>
        <ul style={kc.safetyList}>
          {(items || []).map((s, i) => (
            <li key={i} style={kc.safetyItem}>
              <span style={{ ...kc.safetyMark, color: a }}>{'\u2713'}</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// Related categories - generated dynamically from configuration.
export function RelatedCategories({ categories, onOpen }) {
  return (
    <div style={kc.block}>
      <div style={kc.blockHead}>
        <div>
          <h2 style={kc.h2}>Related Categories</h2>
          <p style={kc.h2Sub}>Explore other areas of the Windows Knowledge Centre.</p>
        </div>
      </div>
      <div style={kc.relGrid}>
        {(categories || []).map(cat => {
          const a = cat.accent || '#f8fafc';
          return (
            <div
              key={cat.id}
              style={{ ...kc.relCard, border: `1px solid ${hexA(a, 0.40)}`, boxShadow: `0 0 0 1px ${hexA(a, 0.14)}` }}
              onClick={() => onOpen && onOpen(cat)}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.borderColor = a;
                e.currentTarget.style.boxShadow = `0 10px 28px rgba(0,0,0,0.44), 0 0 0 1px ${hexA(a, 0.5)}, 0 0 18px ${hexA(a, 0.3)}`;
                const ic = e.currentTarget.querySelector('[data-rel-icon]');
                if (ic) ic.style.filter = 'brightness(1.35) saturate(1.2)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = hexA(a, 0.40);
                e.currentTarget.style.boxShadow = `0 0 0 1px ${hexA(a, 0.14)}`;
                const ic = e.currentTarget.querySelector('[data-rel-icon]');
                if (ic) ic.style.filter = 'none';
              }}
            >
              <span data-rel-icon style={{ ...kc.relIcon, color: a, textShadow: `0 0 10px ${hexA(a, 0.5)}` }}>
                {cat.icon}
              </span>
              <span style={kc.relTitle}>{cat.title}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Reusable FAQ section. Data-driven via the faqs array.
export function FAQSection({ faqs }) {
  return (
    <div style={kc.block}>
      <div style={kc.blockHead}>
        <div>
          <h2 style={kc.h2}>Frequently Asked Questions</h2>
          <p style={kc.h2Sub}>Quick answers about this category and the Knowledge Centre.</p>
        </div>
      </div>
      <div style={kc.faqList}>
        {(faqs || []).map((f, i) => (
          <div key={i} style={kc.faqItem}>
            <div style={kc.faqQ}>{f.q}</div>
            <p style={kc.faqA}>{f.a}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
